import { useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from 'utils/cn'
import { AnchoredPosition, computeAnchoredPosition } from './dropdown-position'
import { Tooltip } from './Tooltip'

// ─────────────────────────────────────────────────────────────────────────────
// Combobox — generic search-as-you-type picker with portal-anchored dropdown.
//
// Handles both single- and multi-select via the `multiple` prop. The component
// owns: search input + chip area, dropdown positioning, keyboard nav (arrows /
// Enter / Tab / Esc / Backspace-to-pop), open/close state.
//
// Consumers own: option shape (generic <T>), how to extract id/label, custom
// option/chip rendering, optional grouping, optional availability filter (for
// patterns like "lock to one subgroup once first item is picked").
//
// Both AgenticEquipmentSelect (multi + groups + DAT lock) and AgenticDriverSelect
// (single) build on this. The pattern matches MUI Autocomplete's API surface but
// renders in slate/blue DS tokens per foundations.md §7 with no MUI dependency.
// ─────────────────────────────────────────────────────────────────────────────

interface ComboboxProps<T> {
    /** Full option set. Filtering by query + availability happens internally. */
    options: T[]
    /** Currently-selected ids. Single-select: array of length 0 or 1. */
    selectedIds: string[]
    onChange: (nextIds: string[]) => void
    /** Extract a stable id from an option. */
    getId: (opt: T) => string
    /** Extract a display label — used for the default substring-match filter and
     *  for the default chip/option renderers when no custom renderer is supplied. */
    getLabel: (opt: T) => string

    multiple?: boolean
    /** Mirrors the internal text query for server-backed search pickers. */
    onQueryChange?: (query: string) => void

    /** Filter option visibility. Defaults to substring-match on getLabel(). */
    filterOption?: (opt: T, query: string) => boolean
    /** Return a group label for each option, or null for "no group". When present,
     *  options are bucketed by group label and renderGroupHeader runs per bucket. */
    groupBy?: (opt: T) => string | null
    renderGroupHeader?: (group: string) => ReactNode
    /** Custom dropdown option renderer. Receives `highlighted` + `selected` flags. */
    renderOption?: (opt: T, ctx: { highlighted: boolean; selected: boolean }) => ReactNode
    /**
     * Mark an option as not selectable. It still RENDERS — callers disable rather than filter when
     * the reason matters to the user (a terminated driver who cannot be charged, say), and a name
     * that silently vanishes from a dropdown reads as a bug to whoever is looking for it.
     *
     * A disabled option cannot be committed by click or by keyboard, and is skipped when arrowing
     * through the list, so it can never become the highlighted-then-Entered value either. Style it
     * via `renderOption`; this governs behaviour only.
     */
    isOptionDisabled?: (opt: T) => boolean
    /** Custom chip renderer for the selected-items area. Receives an onRemove handler
     *  pre-bound to this option's id. */
    renderChip?: (opt: T, onRemove: () => void) => ReactNode
    /** When supplied, options for which this returns false are hidden from the dropdown.
     *  Used by AgenticEquipmentSelect to enforce the DAT class/subtype lock. */
    isOptionAvailable?: (opt: T) => boolean

    /** Max chips visible inline. Overflow renders a "+N" pill. Single-select ignores this. */
    maxVisibleChips?: number

    placeholder?: string
    /** `id` of the text input, so a `<label htmlFor>` elsewhere can name the control. */
    inputId?: string
    disabled?: boolean
    error?: boolean
    /** `sm` (default) is the compact filter-row control; `md` matches DS `Input` in forms and shows a
     *  single selection as plain text instead of a chip. */
    size?: 'sm' | 'md'
    /** Inline width on the outer wrapper. Defaults to 100% of parent. */
    width?: number | string
    /** Min dropdown width (in px). Defaults to anchor width. */
    minDropdownWidth?: number
    /** Empty-list text — shown when `options.length === 0`. */
    emptyText?: string
    /** No-matches text — shown when filtering produces zero results. Defaults to "No matches". */
    noMatchesText?: string

    /** Optional label rendered above the input — convenience for filter rows that
     *  need the same `text-[11px] font-medium text-slate-500` label as DS Input. */
    label?: string
    inputTestId?: string
    autoFocus?: boolean
    /** className passthrough on the outer wrapper. */
    className?: string
}

type DropdownPos = AnchoredPosition

export function Combobox<T>({
    options,
    selectedIds,
    onChange,
    getId,
    getLabel,
    multiple = false,
    onQueryChange,
    filterOption,
    groupBy,
    renderGroupHeader,
    renderOption,
    isOptionDisabled,
    renderChip,
    isOptionAvailable,
    maxVisibleChips = 2,
    placeholder,
    inputId,
    disabled = false,
    error = false,
    size = 'sm',
    width,
    minDropdownWidth,
    emptyText,
    noMatchesText = 'No matches',
    label,
    inputTestId,
    autoFocus = false,
    className = '',
}: ComboboxProps<T>) {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [highlightIdx, setHighlightIdx] = useState(0)
    const [dropdownPos, setDropdownPos] = useState<DropdownPos | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const anchorRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLDivElement>(null)

    const setQueryValue = useCallback(
        (nextQuery: string) => {
            setQuery(nextQuery)
            onQueryChange?.(nextQuery)
        },
        [onQueryChange]
    )

    const optionById = useMemo(() => {
        const m = new Map<string, T>()
        for (const o of options) m.set(getId(o), o)
        return m
    }, [options, getId])

    const selectedOptions = useMemo<T[]>(() => selectedIds.map((id) => optionById.get(id)).filter((o): o is T => !!o), [selectedIds, optionById])

    // Build the visible-option list: drop ones filtered out by `isOptionAvailable`,
    // already-selected (only for multi), then apply the query filter.
    const visibleOptions = useMemo<T[]>(() => {
        const matchFn = filterOption ?? ((opt: T, q: string) => getLabel(opt).toLowerCase().includes(q.toLowerCase()))
        return options.filter((o) => {
            if (isOptionAvailable && !isOptionAvailable(o)) return false
            if (multiple && selectedIds.includes(getId(o))) return false
            if (query && !matchFn(o, query)) return false
            return true
        })
    }, [options, isOptionAvailable, multiple, selectedIds, query, filterOption, getId, getLabel])

    // Group the visible options if groupBy is supplied; otherwise treat them as one
    // flat list. Bucket order = first appearance in `options`.
    const grouped = useMemo<{ group: string | null; items: T[] }[]>(() => {
        if (!groupBy) return [{ group: null, items: visibleOptions }]
        const buckets: { group: string | null; items: T[] }[] = []
        const indexOf = new Map<string, number>()
        for (const o of visibleOptions) {
            const g = groupBy(o)
            const key = g ?? '__none__'
            if (!indexOf.has(key)) {
                indexOf.set(key, buckets.length)
                buckets.push({ group: g, items: [] })
            }
            buckets[indexOf.get(key)!].items.push(o)
        }
        return buckets
    }, [visibleOptions, groupBy])

    // Reset the highlight when the option set changes (search, lock-change, etc).
    useEffect(() => {
        setHighlightIdx(0)
    }, [query, visibleOptions.length])

    // Close on click outside the anchor (input + chip strip) or the portal'd list.
    // Capture phase is load-bearing: an option's React onMouseDown calls onChange,
    // which in multi-select removes the just-picked item from the visible list and
    // unmounts the clicked button before the document bubble listener can check
    // listRef.contains(target). Reading the target in capture phase — before any
    // synthetic handler runs — keeps the original DOM target intact.
    useEffect(() => {
        if (!open) return
        const onMouseDown = (e: MouseEvent) => {
            const target = e.target as Node | null
            if (!target) return
            if (anchorRef.current?.contains(target)) return
            if (listRef.current?.contains(target)) return
            setOpen(false)
        }
        document.addEventListener('mousedown', onMouseDown, true)
        return () => document.removeEventListener('mousedown', onMouseDown, true)
    }, [open])

    // Recompute the portal dropdown position whenever it opens or the window resizes.
    useEffect(() => {
        if (!open || !anchorRef.current) {
            setDropdownPos(null)
            return
        }
        const update = () => {
            if (!anchorRef.current) return
            const rect = anchorRef.current.getBoundingClientRect()
            const width = Math.max(rect.width, minDropdownWidth ?? rect.width)
            setDropdownPos(computeAnchoredPosition(rect, { desiredMax: 360, width }))
        }
        update()
        window.addEventListener('resize', update)
        window.addEventListener('scroll', update, true)
        return () => {
            window.removeEventListener('resize', update)
            window.removeEventListener('scroll', update, true)
        }
    }, [open, minDropdownWidth])

    const addOption = useCallback(
        (opt: T) => {
            // Refused before anything is committed AND before the dropdown closes: closing on a
            // rejected pick looks identical to a successful one, so the user is left believing they
            // chose someone. Keeping it open leaves the disabled row and its reason on screen.
            if (isOptionDisabled?.(opt)) return
            const id = getId(opt)
            if (multiple) {
                if (!selectedIds.includes(id)) onChange([...selectedIds, id])
            } else {
                onChange([id])
                setOpen(false)
            }
            setQueryValue('')
        },
        [multiple, selectedIds, onChange, getId, isOptionDisabled, setQueryValue]
    )

    const removeId = useCallback(
        (id: string) => {
            onChange(selectedIds.filter((s) => s !== id))
        },
        [selectedIds, onChange]
    )

    const clearAll = useCallback(() => {
        onChange([])
        setQueryValue('')
    }, [onChange, setQueryValue])

    useEffect(() => {
        if (!autoFocus || disabled) return
        inputRef.current?.focus()
        setOpen(true)
    }, [autoFocus, disabled])

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (!open || visibleOptions.length === 0) {
                // Backspace on empty query clears the selection in single-select mode
                // (multi-select keeps chips; the chip × handles removal).
                if (e.key === 'Backspace' && !query && !multiple && selectedIds.length > 0) {
                    clearAll()
                }
                return
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setHighlightIdx((i) => {
                    const next = Math.min(i + 1, visibleOptions.length - 1)
                    scrollHighlightedIntoView(listRef.current, next)
                    return next
                })
            } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setHighlightIdx((i) => {
                    const next = Math.max(i - 1, 0)
                    scrollHighlightedIntoView(listRef.current, next)
                    return next
                })
            } else if (e.key === 'Enter') {
                const opt = visibleOptions[highlightIdx]
                if (opt) {
                    e.preventDefault()
                    addOption(opt)
                }
            } else if (e.key === 'Tab' || e.key === 'Escape') {
                setOpen(false)
            } else if (e.key === 'Backspace' && !query && !multiple && selectedIds.length > 0) {
                clearAll()
            }
        },
        [open, visibleOptions, highlightIdx, addOption, query, multiple, selectedIds, clearAll]
    )

    // Map each visible option to its flat index — needed because rendering loops
    // walk buckets but the highlight index is one flat counter.
    const flatIndexById = useMemo(() => {
        const m = new Map<string, number>()
        visibleOptions.forEach((o, i) => m.set(getId(o), i))
        return m
    }, [visibleOptions, getId])

    const highlightedId = visibleOptions[highlightIdx] ? getId(visibleOptions[highlightIdx]) : null

    const formSize = size === 'md'

    // ── Default renderers (used when consumer omits the custom prop) ────────
    const defaultChip = (opt: T, onRemove: () => void) => (
        <span
            key={getId(opt)}
            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 shrink-0 max-w-[140px]"
        >
            <span className="truncate">{getLabel(opt)}</span>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation()
                    onRemove()
                }}
                className="ml-0.5 w-3 h-3 flex items-center justify-center rounded-full bg-slate-400/50 text-white text-[8px] hover:bg-slate-500 border-none cursor-pointer p-0"
                aria-label="Remove"
            >
                ×
            </button>
        </span>
    )

    const defaultOption = (opt: T, ctx: { highlighted: boolean; selected: boolean }) => (
        <span
            className={cn(
                'block w-full text-left px-3 py-1.5 transition-colors',
                formSize ? 'text-sm' : 'text-xs',
                ctx.highlighted
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    : 'text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800'
            )}
        >
            {getLabel(opt)}
        </span>
    )

    // A form field reads as a value, not a filter tag. Backspace still clears it (see handleKeyDown).
    const plainValue = (opt: T) => (
        <span key={getId(opt)} className="min-w-0 shrink truncate text-sm text-slate-900 dark:text-slate-100">
            {getLabel(opt)}
        </span>
    )
    const chipRenderer = renderChip ?? (formSize && !multiple ? plainValue : defaultChip)
    const optionRenderer = renderOption ?? defaultOption

    const visibleChips = multiple ? selectedOptions.slice(0, maxVisibleChips) : selectedOptions

    return (
        <div className={cn('relative flex flex-col gap-0.5', className)} style={width !== undefined ? { width } : undefined}>
            {label && <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">{label}</span>}
            <div
                ref={anchorRef}
                className={cn(
                    'flex items-center gap-1 rounded-lg border bg-white dark:bg-slate-900 cursor-text overflow-hidden transition-colors',
                    formSize ? 'h-[38px] px-3 shadow-sm' : 'h-8 px-2',
                    disabled && 'opacity-50 cursor-not-allowed',
                    error
                        ? 'border-red-500 ring-2 ring-red-500/20'
                        : open
                          ? 'border-blue-600 ring-2 ring-blue-600/20'
                          : formSize
                            ? 'border-slate-300 dark:border-slate-600'
                            : 'border-slate-200 dark:border-slate-700'
                )}
                onClick={() => !disabled && inputRef.current?.focus()}
            >
                {/* A disabled Combobox must not let its chips be removed. The wrapper only greys
                    out, so the chip's × stayed live and cleared a selection the consumer had
                    deliberately locked — on the adjustment edit modal that read as "assignee
                    cleared" while the save silently kept the original. No-op the remove instead. */}
                {visibleChips.map((opt) => chipRenderer(opt, () => !disabled && removeId(getId(opt))))}
                {multiple && selectedOptions.length > maxVisibleChips && (
                    // Overflow badge — hovering it reveals the hidden selections (the inline chips
                    // are capped at `maxVisibleChips` and the container clips the rest). Portal-based
                    // DS Tooltip so it escapes this box's `overflow-hidden`. The `shrink-0` wrapper
                    // is load-bearing: the Tooltip's trigger span is `min-w-0`, so without it flexbox
                    // shrinks the badge to nothing and the input renders over it.
                    <span className="shrink-0">
                        <Tooltip
                            title={
                                <div className="flex flex-col gap-0.5">
                                    {selectedOptions.slice(maxVisibleChips).map((opt) => (
                                        <span key={getId(opt)}>{getLabel(opt)}</span>
                                    ))}
                                </div>
                            }
                        >
                            <span className="px-1 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-[10px] font-medium text-blue-700 dark:text-blue-300 shrink-0 cursor-default">
                                +{selectedOptions.length - maxVisibleChips}
                            </span>
                        </Tooltip>
                    </span>
                )}
                <input
                    ref={inputRef}
                    type="text"
                    id={inputId}
                    data-testid={inputTestId}
                    value={query}
                    onChange={(e) => setQueryValue(e.target.value)}
                    onFocus={() => {
                        if (!disabled) setOpen(true)
                    }}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    placeholder={selectedOptions.length === 0 ? placeholder : ''}
                    className={cn(
                        'flex-1 min-w-[30px] bg-transparent outline-none text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 disabled:cursor-not-allowed',
                        formSize ? 'text-sm' : 'text-xs font-medium placeholder:font-normal'
                    )}
                />
            </div>
            {open &&
                dropdownPos &&
                createPortal(
                    <div
                        ref={listRef}
                        style={{
                            position: 'fixed',
                            ...(dropdownPos.top !== undefined ? { top: dropdownPos.top } : { bottom: dropdownPos.bottom }),
                            left: dropdownPos.left,
                            width: dropdownPos.width,
                            maxHeight: dropdownPos.maxHeight,
                            zIndex: 9999,
                        }}
                        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg py-1 overflow-y-auto"
                    >
                        {options.length === 0 && emptyText && <div className="px-3 py-2 text-[11px] text-slate-500 dark:text-slate-400">{emptyText}</div>}
                        {options.length > 0 && visibleOptions.length === 0 && (
                            <div className="px-3 py-2 text-[11px] text-slate-500 dark:text-slate-400">{noMatchesText}</div>
                        )}
                        {grouped.map((bucket, bIdx) => (
                            <div key={bucket.group ?? `__none__${bIdx}`}>
                                {bucket.group != null &&
                                    (renderGroupHeader ? (
                                        renderGroupHeader(bucket.group)
                                    ) : (
                                        // `sticky top-0` pins each header to the top of the scrollable
                                        // list while its own bucket scrolls underneath — the next
                                        // bucket's header then displaces it, so the visible header always
                                        // names whichever group is actually in view.
                                        <div className="sticky top-0 z-10 px-3 py-1 text-[10px] font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/90 backdrop-blur-sm uppercase tracking-wider">
                                            {bucket.group}
                                        </div>
                                    ))}
                                {bucket.items.map((opt) => {
                                    const id = getId(opt)
                                    const isHighlighted = highlightedId === id
                                    const flatIdx = flatIndexById.get(id) ?? -1
                                    return (
                                        <button
                                            key={id}
                                            type="button"
                                            data-option-id={id}
                                            onMouseDown={(e) => {
                                                e.preventDefault()
                                                addOption(opt)
                                            }}
                                            onMouseEnter={() => {
                                                if (flatIdx >= 0) setHighlightIdx(flatIdx)
                                            }}
                                            className="w-full bg-transparent border-none p-0 cursor-pointer"
                                        >
                                            {optionRenderer(opt, { highlighted: isHighlighted, selected: false })}
                                        </button>
                                    )
                                })}
                            </div>
                        ))}
                    </div>,
                    document.body
                )}
        </div>
    )
}

// ── Helpers ────────────────────────────────────────────────────────────────

function scrollHighlightedIntoView(list: HTMLDivElement | null, idx: number) {
    if (!list) return
    const items = list.querySelectorAll<HTMLButtonElement>('[data-option-id]')
    items[idx]?.scrollIntoView({ block: 'nearest' })
}
